<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1100px"
    persistent
    scrollable
    data-testid="tcv2-history-compare-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-compare</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ titleText }}</div>
          <div class="text-caption text-white-50">เทียบชุดสูตรของ 2 รอบบันทึก (ซ้าย = รอบเก่ากว่า)</div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="tcv2-compare-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="tcv2-compare-error"
        >
          {{ error }}
        </v-alert>

        <div v-if="loading" class="text-center py-10" data-testid="tcv2-compare-loading">
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="text-caption text-medium-emphasis mt-3 mb-0">กำลังโหลดข้อมูล 2 รอบเพื่อเปรียบเทียบ…</p>
        </div>

        <template v-else-if="diff">
          <div class="d-flex flex-wrap align-center ga-2 mb-4" data-testid="tcv2-compare-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ diff.summary.added }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-minus">
              ลบ {{ diff.summary.removed }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ diff.summary.modified }}
            </v-chip>
            <v-chip variant="tonal" size="small" prepend-icon="mdi-equal">
              เหมือนเดิม {{ diff.summary.unchanged }}
            </v-chip>
            <v-spacer />
            <v-btn-toggle
              v-model="changedOnly"
              color="primary"
              density="comfortable"
              variant="outlined"
              mandatory
              data-testid="tcv2-compare-filter"
            >
              <v-btn :value="true" size="small">แสดงเฉพาะที่เปลี่ยน</v-btn>
              <v-btn :value="false" size="small">แสดงทั้งหมด</v-btn>
            </v-btn-toggle>
          </div>

          <div class="text-subtitle-2 font-weight-medium mb-2">
            ฟังก์ชัน
            <span class="text-caption text-medium-emphasis">({{ visibleRows.length }})</span>
          </div>

          <p v-if="!visibleRows.length" class="text-caption text-medium-emphasis mb-0">
            {{ changedOnly ? 'ไม่มีการเปลี่ยนแปลงระหว่าง 2 รอบนี้' : 'ไม่มีสูตรในทั้ง 2 รอบ' }}
          </p>

          <v-expansion-panels v-else variant="accordion" multiple data-testid="tcv2-compare-functions">
            <v-expansion-panel v-for="row in visibleRows" :key="row.key">
              <v-expansion-panel-title>
                <div class="d-flex align-center flex-wrap ga-2 w-100">
                  <v-icon :color="stateColor(row.state)" size="18">{{ stateIcon(row.state) }}</v-icon>
                  <span class="font-weight-medium">{{ row.label || '(ไม่มีชื่อ)' }}</span>
                  <v-chip v-if="row.functionCode" size="x-small" variant="tonal">
                    {{ row.functionCode }}
                  </v-chip>
                  <v-chip
                    v-if="row.fieldKey"
                    size="x-small"
                    variant="text"
                    class="text-medium-emphasis"
                  >
                    {{ row.fieldKey }}
                  </v-chip>
                  <v-spacer />
                  <v-chip size="x-small" variant="tonal" :color="stateColor(row.state)">
                    {{ stateLabel(row.state) }}
                    <template v-if="row.fields.length"> · {{ row.fields.length }} ฟิลด์</template>
                  </v-chip>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <p v-if="!row.fields.length" class="text-caption text-medium-emphasis mb-0">
                  ไม่มีการเปลี่ยนแปลง
                </p>
                <!-- The table is the only thing allowed to scroll sideways: `sourceCode` is a whole
                     C# body, and letting it push the dialog wide would scroll the PAGE instead. -->
                <div v-else class="tcv2-compare-scroll">
                  <v-table density="compact" class="tcv2-compare-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 200px">ฟิลด์</th>
                        <th class="text-left">v{{ leftNo }}</th>
                        <th class="text-left">v{{ rightNo }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in row.fields" :key="row.key + '|' + f.name">
                        <td class="font-weight-medium">{{ f.name }}</td>
                        <td>
                          <div :class="valueClass(f.name)">{{ showValue(f.left, f.name) }}</div>
                        </td>
                        <td>
                          <div :class="valueClass(f.name)">{{ showValue(f.right, f.name) }}</div>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" color="primary" data-testid="tcv2-compare-close" @click="close">
          <v-icon start>mdi-close</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Compare two save-history snapshots of ONE Tax Calculate V2 version, side by side.
 *
 * The parent (the history dialog) hands over the two ticked `snapshotID`s in whatever order the
 * admin ticked them; this dialog fetches both payloads in parallel and puts the LOWER
 * `snapshotNo` on the left, so the table always reads older → newer no matter how it was opened.
 *
 * All diffing is client-side (`utils/taxCalcSnapshotDiff.js`) — there is no compare endpoint. That
 * util compares the UNION of the keys on both sides (minus the audit/compile-cache noise), so a
 * column added to `tax.TaxCalcFunctionV2` later shows up here with no change to this file.
 *
 * The one thing this screen has that the question compare does not: a formula's `sourceCode` is a
 * long multi-line C# body. It is rendered `pre-wrap` inside its own scrolling box — readable, and
 * unable to widen the dialog.
 */
import taxCalculateV2Api from '@/services/taxCalculateV2Api';
import { diffSnapshots, truncateValue } from '@/utils/taxCalcSnapshotDiff';

const STATE_ICON = {
  added: 'mdi-plus',
  removed: 'mdi-minus',
  modified: 'mdi-pencil',
  unchanged: 'mdi-equal',
};
const STATE_COLOR = {
  added: 'success',
  removed: 'error',
  modified: 'primary',
  unchanged: 'grey',
};
const STATE_LABEL = {
  added: 'เพิ่ม',
  removed: 'ลบ',
  modified: 'แก้',
  unchanged: 'เหมือนเดิม',
};

/**
 * Fields rendered as CODE (monospace, own scroll box) rather than as a one-liner. `sourceCode` is
 * the reason; the JSON blobs read the same way. Compared lowercase — a payload written before the
 * camelCase policy can arrive PascalCase.
 */
const CODE_FIELDS = new Set(['sourcecode', 'parameterbindingsjson', 'parametersjson']);

/** Long enough to read a whole formula, short enough that 50 panels cannot lock up the DOM. */
const CODE_MAX = 6000;
const TEXT_MAX = 300;

export default {
  name: 'TaxCalcHistoryCompareV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** snapshotID of one ticked row (either side — sorted by snapshotNo on load) */
    leftSnapshotId: { type: Number, default: null },
    /** snapshotID of the other ticked row */
    rightSnapshotId: { type: Number, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      loading: false,
      error: '',
      diff: null,
      leftNo: '',
      rightNo: '',
      changedOnly: true,
    };
  },
  computed: {
    titleText() {
      if (this.leftNo === '' || this.rightNo === '') return 'เทียบ 2 รอบบันทึก';
      return `เทียบ v${this.leftNo} ↔ v${this.rightNo}`;
    },
    visibleRows() {
      const list = this.diff && Array.isArray(this.diff.functions) ? this.diff.functions : [];
      return this.changedOnly ? list.filter((r) => r.state !== 'unchanged') : list;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
      else this.reset();
    },
  },
  methods: {
    stateIcon(state) {
      return STATE_ICON[state] || 'mdi-help';
    },
    stateColor(state) {
      return STATE_COLOR[state] || 'grey';
    },
    stateLabel(state) {
      return STATE_LABEL[state] || state;
    },
    isCodeField(name) {
      return CODE_FIELDS.has(String(name || '').toLowerCase());
    },
    valueClass(name) {
      return this.isCodeField(name) ? 'tcv2-compare-value tcv2-compare-code' : 'tcv2-compare-value';
    },
    /** Empty string on a side means «this side has no value» — say so instead of showing a gap. */
    showValue(v, name) {
      const s = truncateValue(v, this.isCodeField(name) ? CODE_MAX : TEXT_MAX);
      return s === '' ? '—' : s;
    },
    reset() {
      this.diff = null;
      this.error = '';
      this.leftNo = '';
      this.rightNo = '';
      this.changedOnly = true;
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async load() {
      this.reset();
      if (!this.leftSnapshotId || !this.rightSnapshotId) {
        this.error = 'ต้องเลือก 2 รอบเพื่อเปรียบเทียบ';
        return;
      }
      this.loading = true;
      try {
        const [a, b] = await Promise.all([
          taxCalculateV2Api.getSnapshot(this.leftSnapshotId),
          taxCalculateV2Api.getSnapshot(this.rightSnapshotId),
        ]);
        // Older round on the left, whatever order the checkboxes were ticked in.
        const older = (a.snapshotNo ?? 0) <= (b.snapshotNo ?? 0) ? a : b;
        const newer = older === a ? b : a;
        this.leftNo = older.snapshotNo;
        this.rightNo = newer.snapshotNo;
        this.diff = diffSnapshots(older.payloadJson, newer.payloadJson);
      } catch (e) {
        this.diff = null;
        this.error = (e && e.message) || 'โหลดข้อมูลเปรียบเทียบไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
/* Sideways overflow is contained here — never handed to the page. */
.tcv2-compare-scroll {
  overflow-x: auto;
  max-width: 100%;
}
.tcv2-compare-table {
  table-layout: fixed;
  width: 100%;
}
.tcv2-compare-table :deep(td),
.tcv2-compare-table :deep(th) {
  vertical-align: top;
}
.tcv2-compare-value {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 380px;
}
/* A C# body: monospace, and capped so one long formula cannot make the panel endless. */
.tcv2-compare-code {
  font-family: 'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace;
  font-size: 0.75rem;
  line-height: 1.45;
  max-height: 260px;
  overflow: auto;
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 4px;
  padding: 6px 8px;
}
</style>
